import type { DriverComplianceSummary, RequirementStatus } from '@milepost/shared';

/**
 * Typed fetch client.
 *
 * Session auth is an httpOnly cookie, so every request sends credentials and nothing
 * touches localStorage — a token in localStorage is readable by any script that gets onto
 * the page, and this app displays driver PII.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly details: Array<{ field: string; message: string }>;

  constructor(status: number, message: string, details: Array<{ field: string; message: string }> = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }

  /** Field-keyed messages, for showing errors next to the input that caused them. */
  byField(): Record<string, string> {
    return Object.fromEntries(this.details.map((d) => [d.field, d.message]));
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json() : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error ?? `Request failed (${res.status})`,
      payload?.details ?? [],
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
};

// ── Shapes returned by the API ──────────────────────────────────────────────────

export type Role = 'platform_admin' | 'carrier_admin' | 'safety_manager' | 'read_only' | 'driver';

export interface Me {
  user: { id: string; email: string; fullName: string; role: Role; tenantId: string | null };
  tenant: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    status: string;
    dot_number: string | null;
  } | null;
  impersonation: { by: string; reason: string | null } | null;
}

export interface RosterTotals {
  drivers: number;
  compliant: number;
  /** Distinct drivers with a deficient file — not withExpired + withMissing, which double-counts. */
  nonCompliant: number;
  withExpired: number;
  withMissing: number;
  expiringSoon: number;
  pendingReview: number;
}

export interface RosterDriver {
  id: string;
  employeeId: string | null;
  firstName: string;
  lastName: string;
  driverClass: 'cdl' | 'clp' | 'non_cdl';
  status: string;
  hireDate: string | null;
  compliant: boolean;
  counts: DriverComplianceSummary['counts'];
  nextDueAt: string | null;
  headline: { status: RequirementStatus; title: string; cfrCite: string } | null;
}

export interface Roster {
  asOf: string;
  totals: RosterTotals;
  drivers: RosterDriver[];
}

export interface ArtifactRow {
  id: string;
  requirement_code: string;
  artifact_kind: 'document' | 'record' | 'attestation';
  sensitivity: 'normal' | 'restricted';
  effective_at: string | null;
  expires_at: string | null;
  review_state: 'pending_review' | 'accepted' | 'rejected' | 'superseded';
  mime_type: string | null;
  byte_size: string | null;
  original_filename: string | null;
  record_data: Record<string, unknown>;
  uploaded_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  retention_expires_at: string | null;
  has_file: boolean;
}

export interface DriverDetail {
  driver: {
    id: string;
    employeeId: string | null;
    firstName: string;
    lastName: string;
    driverClass: 'cdl' | 'clp' | 'non_cdl';
    isInterstate: boolean;
    hasSpeCertificate: boolean;
    hasMedicalExemption: boolean;
    status: string;
    hireDate: string | null;
    terminationDate: string | null;
    email: string | null;
    phone: string | null;
  };
  compliance: DriverComplianceSummary;
  artifacts: ArtifactRow[];
}

export interface CatalogEntry {
  code: string;
  title: string;
  cfrCite: string | null;
  description: string;
  note: string | null;
  artifactKind: 'document' | 'record' | 'attestation';
  responsibleParty: 'carrier' | 'driver' | 'either';
  sensitivity: 'normal' | 'restricted';
  recurrence: string;
  disqualifying: boolean;
}

export interface PendingReviewItem {
  id: string;
  driver_id: string;
  first_name: string;
  last_name: string;
  requirement_code: string;
  artifact_kind: string;
  sensitivity: string;
  effective_at: string | null;
  expires_at: string | null;
  mime_type: string | null;
  byte_size: string | null;
  uploaded_at: string;
}

// ── Platform admin console ──────────────────────────────────────────────────────
//
// Milepost staff, across every carrier. These shapes are aggregate by design: there is no
// driver name, document, or roster here, because `platform_admin` does not grant ambient
// access to a carrier's records. Reaching those means starting an audited impersonation,
// which drops the operator into the ordinary carrier app under the carrier's own RLS.

export interface PlatformSummary {
  tenantsTotal: number;
  tenantsActive: number;
  tenantsTrialing: number;
  tenantsPastDue: number;
  tenantsSuspended: number;
  tenantsCanceled: number;
  driversTotal: number;
  driversEvaluated: number;
  driversNonCompliant: number;
  staffTotal: number;
  pendingReviewTotal: number;
  alertsBounced7d: number;
  events24h: number;
  lastEvaluatedFor: string | null;
  /** Oldest per-carrier evaluation date — the number that says whether the scan is keeping up. */
  stalestEvaluatedFor: string | null;
}

export interface TenantRollup {
  id: string;
  name: string;
  slug: string;
  dotNumber: string | null;
  status: string;
  timezone: string;
  createdAt: string;
  /** The carrier's digest recipient — their primary contact. Never a driver address. */
  alertEmail: string | null;
  /** Assigned Milepost account owner. Free text; may be unset. */
  accountOwner: string | null;
  subscriptionStatus: string;
  seats: number;
  delinquentSince: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  /** Present only so the console can link out to Stripe; billing is never written from here. */
  stripeCustomerId: string | null;
  staffActive: number;
  driverLoginsActive: number;
  drivers: { active: number; applicant: number; inactive: number; terminated: number };
  driversEvaluated: number;
  driversCompliant: number;
  driversNonCompliant: number;
  requirements: { expiringSoon: number; expired: number; missing: number; pendingReview: number };
  pendingReviewArtifacts: number;
  /** Null means never evaluated — which is not the same claim as "compliant". */
  lastEvaluatedFor: string | null;
  lastActivityAt: string | null;
  events7d: number;
  alertsBounced7d: number;
}

export interface RequirementGap {
  requirementCode: string;
  title: string;
  cfrCite: string | null;
  status: RequirementStatus;
  driverCount: number;
}

export interface StaffAccount {
  id: string;
  email: string;
  fullName: string;
  role: string;
  lastLoginAt: string | null;
  failedLoginCount: number;
  lockedUntil: string | null;
  deactivatedAt: string | null;
  createdAt: string;
  activeSessions: number;
}

export interface ActivityEvent {
  id: number;
  tenantId: string | null;
  tenantName: string | null;
  action: string;
  objectType: string | null;
  objectId: string | null;
  actorUserId: string | null;
  actorRole: string | null;
  /** Null for driver actors — masked server-side, since a driver's email is driver PII. */
  actorEmail: string | null;
  actorName: string | null;
  onBehalfOf: string | null;
  ip: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface ActivityPage {
  events: ActivityEvent[];
  nextBefore: number | null;
}

export interface AdminOverview {
  summary: PlatformSummary;
  tenants: TenantRollup[];
}

export interface AdminCustomer {
  tenant: TenantRollup;
  gaps: RequirementGap[];
  staff: StaffAccount[];
  activity: ActivityPage;
}
